import type { ModelCandidate } from "@/services/adminModelCurrency";
import { cn } from "@/lib/utils";
import CandidateActions from "./CandidateActions";
import ProviderChip from "./ProviderChip";
import RunHint from "./RunHint";
import CandidatePrice from "./CandidatePrice";
import ComparisonLine from "./ComparisonLine";
import EvalChecks from "./EvalChecks";
import StatusBadge from "./StatusBadge";
import { shouldShowRunHint, summaryText } from "./helpers";
import { candidateTone, promoteBlockedReason, type CandidateTone } from "./layoutHelpers";
import { formatDateTime } from "../programNews/formatters";

interface CandidateRowProps {
  candidate: ModelCandidate;
  /** The model this feature runs on today; results tested against another model are out of date. */
  currentModel: string;
  writable: boolean;
  onPromote: (candidateId: string) => Promise<unknown>;
  onDismiss: (candidateId: string) => Promise<unknown>;
  onRunTest: (candidateId: string) => Promise<unknown>;
}

const KIND_LABELS: Record<ModelCandidate["kind"], string> = {
  newer: "Newer model",
  replacement: "Replacement for a retiring model",
};

/** Left edge color: green ready, red problem, grey still waiting. */
const TONE_EDGE: Record<CandidateTone, string> = {
  ready: "border-l-emerald-500",
  problem: "border-l-destructive",
  pending: "border-l-border",
  closed: "border-l-border",
};

const CandidateRow = ({
  candidate,
  currentModel,
  writable,
  onPromote,
  onDismiss,
  onRunTest,
}: CandidateRowProps) => {
  const results = candidate.evalResults;
  const tone = candidateTone(candidate, currentModel);
  const blocked = writable ? promoteBlockedReason(candidate, currentModel) : null;
  return (
    <li className={cn("space-y-2.5 rounded-md border border-l-4 bg-card p-3", TONE_EDGE[tone])}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm font-medium">{candidate.model}</span>
            <ProviderChip provider={candidate.provider} />
            <StatusBadge status={candidate.status} />
          </div>
          <p className="text-xs text-muted-foreground">
            {KIND_LABELS[candidate.kind]} · found {formatDateTime(candidate.detectedAt)}
          </p>
        </div>
        <CandidateActions
          candidate={candidate}
          writable={writable}
          currentModel={currentModel}
          promoteBlocked={!!blocked}
          onPromote={onPromote}
          onDismiss={onDismiss}
          onRunTest={onRunTest}
        />
      </div>
      <ComparisonLine candidate={candidate} />
      {candidate.recommendation ? (
        <p className="text-sm text-muted-foreground">{candidate.recommendation}</p>
      ) : null}
      {results ? (
        <div className="space-y-1.5">
          {summaryText(results.summary) ? (
            <p className="text-xs text-muted-foreground">{summaryText(results.summary)}</p>
          ) : null}
          <EvalChecks checks={results.checks ?? []} />
        </div>
      ) : null}
      <CandidatePrice candidate={candidate} />
      {shouldShowRunHint(candidate) && candidate.runHint ? (
        <RunHint hint={candidate.runHint} />
      ) : null}
      {blocked ? <p className="text-xs text-muted-foreground">{blocked}</p> : null}
    </li>
  );
};

export default CandidateRow;
